import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { transformedValue, type FormValueControl } from '@angular/forms/signals';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { DateRange, MatCalendar } from '@angular/material/datepicker';
import { DateAdapter } from '@angular/material/core';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';

import { UiFieldControl } from '../../../basics/ui-field-control';
import { IconUIComponent } from '../../atoms/icon/icon.component';
import { UiFieldActionDirective } from '../../../directives';
import { ValidationErrorUIComponent } from '../../atoms/validation-error/validation-error.component';
import { getRandomId } from '../../../utils/get-random-id';

import { patternForMode, resolveDateFormatProfile } from '../../../constants';
import { compilePattern, compileRangePattern } from './_core/pattern-compiler';
import {
  applyArrow,
  applyChar,
  applyDelete,
  applyPaste,
  ArrowDirection,
  autocompleteTime,
  commitPartialFields,
  clampOverflowDays,
  DeleteDirection,
  orderRangeSides,
  reconcile,
} from './_core/input-reducers';
import {
  emptySlots,
  fieldForInput,
  firstEditableField,
  nextField,
  prevField,
  renderDisplay,
  selectField,
  slotsAreEmpty,
  slotsFromValues,
} from './_core/slot-layout';
import { parseRangeSlots, parseSlots, reconcileHours } from './_core/value-parser';
import {
  DatePickerValue,
  DateRangeValue,
  formatOutput,
  formatRangeOutput,
  parseOutput,
  PickerMode,
} from './_core/control-value';
import {
  ALL_SIDES,
  Caret,
  EditOptions,
  EMPTY_FIELD_VALUES,
  FieldValues,
  RangeSide,
} from './_core/date-picker.model';
import {
  LAST_HOUR_OF_DAY,
  LAST_MINUTE_OF_HOUR,
  LAST_SECOND_OF_MINUTE,
} from './_core/date-picker.constants';
import { dateToValues, errorKeyOf, pickRangeDate, toDate, valuesEqual } from './_core/picker-rules';
import { ValidationErrorType } from '../../../types';
import { DatePickerAutocomplete, DatePickerEntry, DatePickerSize } from './date-picker.api';
import { KeyNamesEnum } from '../../../constants/key-names.constants';
import { InputEventType } from '../../../constants/input-event-types.constants';
import { DATE_PICKER_LOCALE } from './date-picker-locale.token';
import { DatePickerCalendarHeaderUIComponent } from './_ui/calendar-header/calendar-header.component';
import { DatePickerTimeListUIComponent, TimeSelection } from './_ui/time-list/time-list.component';

const ABSOLUTE_MIN_YEAR = 1900;
const ABSOLUTE_MAX_YEAR = 2100;
const DECEMBER = 11;
const LAST_DAY_OF_DECEMBER = 31;

const ABSOLUTE_MIN = new Date(ABSOLUTE_MIN_YEAR, 0, 1);
const ABSOLUTE_MAX = new Date(
  ABSOLUTE_MAX_YEAR,
  DECEMBER,
  LAST_DAY_OF_DECEMBER,
  LAST_HOUR_OF_DAY,
  LAST_MINUTE_OF_HOUR,
  LAST_SECOND_OF_MINUTE,
);

/** The schema's own error kind, shown only after the first interaction. */
const REQUIRED_ERROR_KIND = 'required';

/** Error keys whose message names the bound the value crossed. */
const BOUND_ERROR_KEYS: ReadonlySet<string> = new Set(['minDate', 'maxDate', 'minTime', 'maxTime']);

/**
 * One picker for date, time, date-time and range.
 *
 * The editable state is a single `slots` signal; the text field, the calendar and the
 * time list are three `computed()` reads of it. They cannot disagree — which is the
 * structural fix for the desync the previous component had between its input string,
 * its shadow copy and its five time signals.
 */
@Component({
  selector: 'fr8bot-ui-date-picker-field',
  imports: [
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    MatCalendar,
    IconUIComponent,
    UiFieldActionDirective,
    ValidationErrorUIComponent,
    DatePickerTimeListUIComponent,
  ],
  templateUrl: './date-picker-field.component.html',
  styleUrl: './date-picker-field.component.scss',
  // The calendar's adapter is provided per field, not taken from the app. Material reads
  // month and weekday names from an ambient `DateAdapter`, so without this a field given
  // its own `locale` drew a Ukrainian mask over an English calendar.
  providers: [provideDateFnsAdapter()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePickerFieldComponent
  extends UiFieldControl
  implements FormValueControl<DatePickerValue>
{
  readonly value = model<DatePickerValue>(null);

  readonly id = input(getRandomId('date-picker'));
  readonly mode = input<PickerMode>('date');
  readonly entry = input<DatePickerEntry>('both');
  readonly label = input<string | null>(null);
  readonly placeholder = input<string | null>(null);
  readonly size = input<DatePickerSize>('large');
  readonly minDate = input<string | Date | null>(null);
  readonly maxDate = input<string | Date | null>(null);
  readonly minuteStep = input(1);
  readonly hourStep = input(1);
  /** Overrides `DATE_PICKER_LOCALE` for this field only. */
  readonly locale = input<string | null>(null);
  /** Overrides the locale's clock, the way the old `hourFormat` input did. */
  readonly hour12 = input<boolean | undefined>(undefined);
  readonly autocomplete = input<DatePickerAutocomplete | undefined>(undefined);

  /** This field's own instance — see the note on `providers` above. */
  private readonly dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private readonly ambientLocale = inject(DATE_PICKER_LOCALE);
  private readonly inputRef = viewChild.required<ElementRef<HTMLInputElement>>('inputEl');

  // Set only while the revalidation effect below re-parses the current slots: `parse` then
  // reports errors without writing the model, which a `computed()` can't do.
  private revalidating = false;

  protected readonly open = signal(false);
  protected readonly focused = signal(false);
  private readonly composing = signal(false);
  private readonly pendingCaret = signal<Caret | null>(null);

  /**
   * The one writable piece of state; everything below is derived from it. Writing the slots
   * parses them into the model, and a model written from outside is laid back out into them,
   * so a value echoed straight back (a filter patched from its own store) changes nothing.
   * Invalid and out-of-range dates reach the bound field as parse errors.
   */
  private readonly slots = transformedValue<DatePickerValue, readonly string[]>(this.value, {
    parse: (slots) => {
      const errorKey = this.computeErrorKey(slots);
      const error = errorKey ? { kind: errorKey } : undefined;

      if (this.revalidating) {
        return { error };
      }

      const next = this.toOutput(slots);

      return { value: valuesEqual(next, this.value()) ? this.value() : next, error };
    },
    format: (value) => this.decode(value),
  });

  protected readonly profile = computed(() =>
    resolveDateFormatProfile(this.locale() ?? this.ambientLocale()),
  );

  // Not `pattern`: `[formField]` owns that input name.
  protected readonly compiledPattern = computed(() => {
    const profile = this.profile();
    const mode = this.mode();

    return mode === PickerMode.Range
      ? compileRangePattern(profile.datePattern, profile.rangeSeparator)
      : compilePattern(patternForMode(profile, mode, this.hour12()));
  });

  private readonly editOptions = computed<EditOptions>(() => ({
    acceptedSeparators: this.profile().acceptedSeparators,
  }));

  protected readonly isEmpty = computed(() => slotsAreEmpty(this.slots()));

  /**
   * An untouched field renders as empty so the browser shows the placeholder; the
   * mask only appears once the user is actually in the field. Rendering a string of
   * spaces instead would suppress the placeholder entirely.
   */
  protected readonly display = computed(() => {
    if (this.isEmpty()) {
      // A field nobody can type into must never show the bare mask: focusing it would
      // reveal `  /  /    ` and read as editable when it is not.
      return this.focused() && this.canUseInput()
        ? renderDisplay(this.compiledPattern(), this.slots())
        : '';
    }

    return renderDisplay(this.compiledPattern(), this.slots());
  });
  protected readonly placeholderText = computed(
    () => this.placeholder() ?? this.compiledPattern().placeholder,
  );

  private readonly parsed = computed(() => parseSlots(this.compiledPattern(), this.slots()));
  private readonly parsedRange = computed(() =>
    parseRangeSlots(this.compiledPattern(), this.slots()),
  );

  /**
   * In range mode this is always a `DateRange`, even while only one end is picked.
   *
   * `MatCalendar._dateSelected` suppresses `selectedChange` when `selected` is a plain
   * `Date` and the user clicks that same date again. Handing it a bare `Date` for a
   * half-open range therefore makes a single-day range (20th to 20th) impossible to
   * complete: the second click on the 20th emits nothing.
   */
  protected readonly calendarValue = computed<DateRange<Date> | Date | null>(() => {
    if (this.mode() !== PickerMode.Range) return this.parsed().date;

    const { from, to } = this.parsedRange();
    if (from.date && to.date) return new DateRange<Date>(from.date, to.date);

    const anchor = from.date ?? to.date ?? null;
    return new DateRange<Date>(anchor, null);
  });

  protected readonly selectedHour = computed(() => this.parsed().values.hour24);
  protected readonly selectedMinute = computed(() => this.parsed().values.minute);

  protected readonly calendarHeader = DatePickerCalendarHeaderUIComponent;

  /** Opens the calendar on the typed date, or on the closest allowed month. */
  protected readonly startAt = computed<Date>(() => {
    const current =
      this.mode() === PickerMode.Range
        ? (this.parsedRange().from.date ?? this.parsedRange().to.date)
        : this.parsed().date;

    if (current) return current;

    const now = new Date();
    if (now.getTime() < this.minDateValue().getTime()) return this.minDateValue();
    if (now.getTime() > this.maxDateValue().getTime()) return this.maxDateValue();
    return now;
  });

  protected readonly minDateValue = computed(() => toDate(this.minDate()) ?? ABSOLUTE_MIN);
  protected readonly maxDateValue = computed(() => toDate(this.maxDate()) ?? ABSOLUTE_MAX);

  protected readonly canUseInput = computed(
    () => !this.disabled() && this.entry() !== DatePickerEntry.Picker,
  );
  protected readonly canUsePanel = computed(
    () => !this.disabled() && this.entry() !== DatePickerEntry.Input,
  );

  private toOutput(slots: readonly string[]): DatePickerValue {
    const mode = this.mode();
    const pattern = this.compiledPattern();

    if (slotsAreEmpty(slots)) return null;

    if (mode === PickerMode.Range) {
      const { from, to } = parseRangeSlots(pattern, slots);

      // Each end is held to the same strictness as a single date below.
      return formatRangeOutput(
        from.date ? from.values : EMPTY_FIELD_VALUES,
        to.date ? to.values : EMPTY_FIELD_VALUES,
      );
    }

    const parsed = parseSlots(pattern, slots);

    // Nothing half-entered reaches the form. `values` carries whatever has been typed so
    // far, digit by digit: a lone `1` on the way to December would go out as January, and
    // a blank time as the midnight nobody chose.
    if (!parsed.complete) return null;

    // Complete is not the same as real: `02/31/2026` fills every slot, and the `Date`
    // `formatOutput` builds from it rolls over to March 3.
    if (pattern.hasDate && !parsed.date) return null;

    return formatOutput(
      parsed.values,
      mode,
      this.autocomplete() === DatePickerAutocomplete.To ? LAST_SECOND_OF_MINUTE : 0,
    );
  }

  private readonly errorKey = computed<string | null>(() => this.computeErrorKey(this.slots()));

  /**
   * The bound that was crossed, formatted for the message: `Min time: 4:58 PM` tells the
   * user what to do, where `Time is earlier than allowed` only says they are wrong.
   *
   */
  private readonly errorBound = computed<string | null>(() => {
    const key = this.errorKey();
    if (!key || !BOUND_ERROR_KEYS.has(key)) return null;

    const date = key.startsWith('min') ? this.minDateValue() : this.maxDateValue();

    // TODO: Vit: Review and test this
    // A medium date over a short one: the latter abbreviates the year (`1/1/26`), which
    // is the one thing a bound must not leave ambiguous. The old picker hardcoded `M/d/y`
    // and so showed US ordering to every locale — these two follow the field's locale.
    //
    // `Intl` rather than Angular's `formatDate`: the latter throws on any tag missing from
    // `registerLocaleData`, and `es-MX` is one of those. The profile id is used instead of
    // the raw input because it is always a tag the registry knows.
    const options: Intl.DateTimeFormatOptions = key.endsWith('Time')
      ? { timeStyle: 'short' }
      : { dateStyle: 'medium' };

    return new Intl.DateTimeFormat(this.profile().id, options).format(date);
  });

  /**
   * The field's own error shows as soon as it exists; one from the form schema (`required`)
   * waits for the first interaction.
   */
  protected readonly displayedError = computed<ValidationErrorType | null>(() => {
    const key = this.errorKey();
    if (key) return { key, value: this.errorValue(key) };

    return this.isEmpty() &&
      this.touched() &&
      this.errors().some((e) => e.kind === REQUIRED_ERROR_KIND)
      ? { key: REQUIRED_ERROR_KIND, value: true }
      : null;
  });

  constructor() {
    super();

    // Material has no input for this: the calendar asks its adapter. Setting it here is
    // what makes `locale` govern the whole field and not just the text mask.
    effect(() => this.dateAdapter.setLocale(this.profile().dateFns));

    // Parse errors are only produced by a write, so a value that arrives already out of range
    // — or a bound that moves past the current value — would leave the field valid. Re-parsing
    // the same slots refreshes the errors without writing the model.
    effect(() => {
      this.value();
      this.minDateValue();
      this.maxDateValue();

      untracked(() => {
        this.revalidating = true;
        this.slots.set(this.slots());
        this.revalidating = false;
      });
    });

    // Runs after Angular has written `[value]`, which `setTimeout` cannot guarantee.
    // Deliberately not gated on `focused()`: changing the value resets the browser's
    // caret to the end of the text, so failing to restore it here leaves the caret
    // past the last field and every following keystroke is dropped.
    afterRenderEffect(() => {
      const caret = this.pendingCaret();
      if (!caret) return;

      untracked(() => {
        this.pendingCaret.set(null);
        this.inputRef().nativeElement.setSelectionRange(caret[0], caret[1]);
      });
    });
  }

  protected onBeforeInput(event: InputEvent): void {
    if (!this.canUseInput()) {
      event.preventDefault();
      return;
    }

    const handled = this.handleInputEvent(event);
    // Anything not handled here — IME composition, undo/redo, autofill — is left to
    // the browser and picked up by `onInput` -> reconcile.
    if (handled) event.preventDefault();
  }

  protected onInput(): void {
    if (!this.canUseInput() || this.composing()) return;

    const raw = this.inputRef().nativeElement.value;
    if (raw === this.display()) return;

    this.commit(reconcile(this.compiledPattern(), raw));
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (!this.canUseInput()) return;

    if (event.key === KeyNamesEnum.ArrowUp || event.key === KeyNamesEnum.ArrowDown) {
      event.preventDefault();
      this.commit(
        applyArrow(
          this.compiledPattern(),
          this.slots(),
          this.caret(),
          event.key === KeyNamesEnum.ArrowUp ? ArrowDirection.Up : ArrowDirection.Down,
          this.fallbackValues(),
        ),
      );
      return;
    }

    if (event.key === KeyNamesEnum.ArrowRight || event.key === KeyNamesEnum.ArrowLeft) {
      const pattern = this.compiledPattern();
      const [start] = this.caret();
      const target =
        event.key === KeyNamesEnum.ArrowRight
          ? nextField(pattern, start)
          : prevField(pattern, start);
      if (!target) return;
      event.preventDefault();
      this.moveCaret(selectField(target), target.index);
      return;
    }

    if (event.key === KeyNamesEnum.Escape && this.open()) {
      event.preventDefault();
      this.open.set(false);
    }
  }

  protected onFocus(): void {
    this.focused.set(true);
    if (!this.canUseInput()) return;

    // Focus lands the caret at the end of the value; activate the first field that
    // still needs input instead.
    const target = firstEditableField(this.compiledPattern(), this.slots());
    if (target) this.pendingCaret.set(selectField(target));
  }

  protected onBlur(): void {
    this.focused.set(false);
    this.markTouched();
    this.settleOnBlur();
  }

  protected onClick(): void {
    if (!this.canUseInput()) {
      // Typing is off, so the field itself is the affordance for opening the panel.
      if (this.canUsePanel()) this.open.set(true);
      return;
    }

    // Snap a click that landed on a separator or past the end onto a real field.
    const [start] = this.caret();
    const target = fieldForInput(this.compiledPattern(), start);
    if (target) this.moveCaret(selectField(target.field), target.field.index);
  }

  protected togglePanel(): void {
    if (!this.canUsePanel()) return;
    this.open.update((open) => !open);
  }

  protected closePanel(): void {
    this.open.set(false);
  }

  protected onCompositionStart(): void {
    this.composing.set(true);
  }

  protected onCompositionEnd(): void {
    this.composing.set(false);
    this.onInput();
  }

  protected onCalendarSelect(date: Date | null): void {
    if (!date || !this.canUsePanel()) return;

    this.markTouched();

    if (this.mode() === PickerMode.Range) {
      this.selectRangeDate(date);
      return;
    }

    const values: FieldValues = {
      ...this.parsed().values,
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };

    this.slots.set(slotsFromValues(this.compiledPattern(), this.slots(), values, ALL_SIDES, false));
    if (!this.compiledPattern().hasTime) this.open.set(false);
  }

  protected onTimeSelect(selection: TimeSelection): void {
    if (!this.canUsePanel()) return;

    this.markTouched();

    const current = this.parsed().values;
    const base = current.year === null && this.compiledPattern().hasDate ? todayValues() : current;

    // The pattern may carry `hh`/`aa` rather than `HH`, so the 12-hour fields have to
    // be derived before writing — otherwise nothing lands in the hour slot at all.
    const values = reconcileHours({
      ...base,
      hour24: selection.hour24,
      hour12: null,
      dayPeriod: null,
      minute: selection.minute,
    });

    this.slots.set(slotsFromValues(this.compiledPattern(), this.slots(), values, ALL_SIDES, false));
  }

  protected clear(): void {
    if (this.disabled()) return;
    this.markTouched();
    this.slots.set(emptySlots(this.compiledPattern()));
  }

  private handleInputEvent(event: InputEvent): boolean {
    const pattern = this.compiledPattern();
    const caret = this.caret();

    switch (event.inputType) {
      case InputEventType.InsertText: {
        const data = event.data ?? '';
        let result = { slots: this.slots(), caret };
        for (const char of data) {
          result = applyChar(pattern, result.slots, result.caret, char, this.editOptions());
        }
        this.commit(result);
        return true;
      }

      case InputEventType.InsertFromPaste:
      case InputEventType.InsertFromDrop: {
        const text = event.data ?? event.dataTransfer?.getData('text') ?? '';
        this.commit(applyPaste(pattern, this.slots(), caret, text));
        return true;
      }

      case InputEventType.DeleteContentBackward:
      case InputEventType.DeleteWordBackward:
      case InputEventType.DeleteSoftLineBackward:
      case InputEventType.DeleteByCut:
        this.commit(applyDelete(pattern, this.slots(), caret, directionOf(event.inputType)));
        return true;

      case InputEventType.DeleteContentForward:
      case InputEventType.DeleteWordForward:
        this.commit(applyDelete(pattern, this.slots(), caret, DeleteDirection.Forward));
        return true;

      default:
        return false;
    }
  }

  private commit(result: { slots: readonly string[]; caret: Caret }): void {
    this.slots.set(result.slots);
    this.pendingCaret.set(result.caret);
  }

  /**
   * A year the user only half-typed is completed the moment the caret leaves it:
   * `26` becomes `2026`. The field being entered is excluded — it is the one still
   * being typed — and `parseSlots` stays strict so nothing is emitted mid-year.
   */
  private settlePartialFields(activeFieldIndex: number | null): void {
    const settled = commitPartialFields(this.compiledPattern(), this.slots(), activeFieldIndex);
    if (settled === this.slots()) return;

    this.slots.set(settled);
  }

  /**
   * Leaving the field is the point where everything half-entered becomes final: partial
   * fields get their padding, a day past the end of its month is clamped to that month's
   * last day, and a range typed back-to-front is put in order. None of it is done on
   * caret moves — someone who typed `31`, or the end date first, is on their way to the
   * rest of the value and should not have it rewritten underneath them.
   */
  private settleOnBlur(): void {
    const pattern = this.compiledPattern();
    const edge = this.autocomplete();

    let working = commitPartialFields(pattern, this.slots());
    if (edge) working = autocompleteTime(pattern, working, edge);

    const settled = orderRangeSides(pattern, clampOverflowDays(pattern, working));
    if (settled !== this.slots()) this.slots.set(settled);
  }

  private moveCaret(caret: Caret, activeFieldIndex: number): void {
    this.settlePartialFields(activeFieldIndex);
    this.pendingCaret.set(caret);
  }

  private caret(): Caret {
    const element = this.inputRef().nativeElement;
    return [element.selectionStart ?? 0, element.selectionEnd ?? 0];
  }

  private markTouched(): void {
    if (!this.touched()) {
      this.touch.emit();
    }
  }

  private selectRangeDate(date: Date): void {
    const picked = pickRangeDate(this.compiledPattern(), this.slots(), this.parsedRange(), date);

    this.slots.set(picked.slots);
    if (picked.complete) this.open.set(false);
  }

  private decode(value: DatePickerValue): readonly string[] {
    const pattern = this.compiledPattern();
    const blank = emptySlots(pattern);

    if (value === null || value === undefined || value === '') return blank;

    if (this.mode() === PickerMode.Range) {
      const range = value as DateRangeValue;
      const from = parseOutput(range?.from, PickerMode.Date);
      const to = parseOutput(range?.to, PickerMode.Date);

      let next = slotsFromValues(
        pattern,
        blank,
        { ...EMPTY_FIELD_VALUES, ...from },
        RangeSide.From,
        true,
      );
      next = slotsFromValues(pattern, next, { ...EMPTY_FIELD_VALUES, ...to }, RangeSide.To, true);
      return next;
    }

    const parsedValue = parseOutput(
      value,
      this.mode() as Exclude<PickerMode, typeof PickerMode.Range>,
    );
    if (!parsedValue) return blank;

    // The control value only ever carries a 24-hour reading, so a pattern written with
    // `hh`/`aa` has nothing to write into its hour and period slots until the 12-hour
    // fields are derived — `10:00` would render as `  :00   `.
    const values = reconcileHours({ ...EMPTY_FIELD_VALUES, ...parsedValue });

    return slotsFromValues(pattern, blank, values, ALL_SIDES, true);
  }

  private fallbackValues(): FieldValues {
    const current = this.parsed().values;
    return current.year === null ? todayValues() : current;
  }

  /**
   * `ValidationErrorPipe` reads the interpolated value out of an object keyed by the error
   * key itself — the shape Angular's own `min`/`maxlength` validators use. Errors with no
   * bound to show stay `true`.
   */
  private errorValue(key: string): true | Record<string, string> {
    const bound = this.errorBound();
    return bound ? { [key]: bound } : true;
  }

  private computeErrorKey(slots: readonly string[]): string | null {
    return errorKeyOf(this.compiledPattern(), slots, {
      mode: this.mode(),
      minDate: this.minDateValue(),
      maxDate: this.maxDateValue(),
      hasMinDate: !!this.minDate(),
      hasMaxDate: !!this.maxDate(),
    });
  }
}

function directionOf(inputType: string): DeleteDirection {
  return inputType === InputEventType.DeleteContentBackward ||
    inputType === InputEventType.DeleteByCut
    ? DeleteDirection.Back
    : DeleteDirection.Word;
}

function todayValues(): FieldValues {
  const now = new Date();
  return {
    ...dateToValues(now),
    hour24: now.getHours(),
    minute: now.getMinutes(),
  };
}
